import { api } from './client';
import { setSession } from '@/auth/store';

export interface TokenPair {
  /** ч.1397: у cookie-сесії (`session === 'cookie'`) обидва токени порожні — вони в httpOnly-cookie. */
  access_token: string;
  refresh_token: string;
  token_type: string;
  user_id?: string | null;
  session?: 'cookie' | 'bearer' | string;
}

/**
 * Відповідь POST /auth/login (Platform Admin Блок A — 2FA).
 *
 * Якщо у user'а увімкнено TOTP (платформенний власник) — backend повертає
 * `requires_2fa=true` + одноразовий `mfa_token` (без access/refresh). Frontend
 * показує другий екран вводу 6-значного коду і викликає POST /auth/login/2fa.
 *
 * Якщо 2FA не потрібна — звичайна відповідь з `access_token`/`refresh_token`
 * (як TokenPair), `requires_2fa=false`, `mfa_token=null`.
 */
export interface LoginResult {
  requires_2fa: boolean;
  mfa_token: string | null;
  access_token: string | null;
  refresh_token: string | null;
  user_id?: string | null;
  session?: 'cookie' | 'bearer' | string;
}

/** GET /auth/platform-status — чи user платформенний власник + стан TOTP. */
export interface PlatformStatus {
  is_support_operator?: boolean;
  /** ч.1319: редактор відео-інструкцій — у панелі бачить лише розділ відео. */
  is_video_editor?: boolean;
  is_platform_owner: boolean;
  totp_enabled: boolean;
  /** ч.1124: панель платформи пускає лише вхід, підтверджений кодом
   *  двофакторного входу; true — цей вхід ще не підтверджено. */
  second_step_required?: boolean;
}

/** POST /auth/2fa/setup — секрет + otpauth:// URI для QR. */
export interface TotpSetupResult {
  secret: string;
  provisioning_uri: string;
}

/**
 * Відповідь GET /auth/me (з Порції 3.0.5.0 — tenant-scoped).
 *
 * Endpoint потребує `X-Tenant-Id`; повертає user-профіль + tenant_role +
 * агрегований список permissions для поточного tenant'а.
 */
export interface MeResponse {
  id: string;
  full_name: string;
  email: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  tenant_role: 'OWNER' | 'ADMIN' | 'MEMBER';
  permissions: string[];
  /**
   * Per-org permissions map (UX α 2026-05-21). Frontend читає для
   * OrgSelector-фільтра у формах ПКО/Реалізації/тощо — щоб user
   * бачив у dropdown'і лише ті org, де реально має потрібний permission.
   * Семантика: { "<organization_id>": ["perm.code.a", "perm.code.b", ...] }.
   * • OWNER/ADMIN — усі org tenant'а з повним набором.
   * • MEMBER — лише org з user_access, з permissions per-org.
   * • UNION values == set(permissions) (інваріант з backend).
   *
   * Поле optional на frontend-стороні щоб уникнути синхронного зрушення
   * усіх mock'ів у тестах. Споживачі (usePermissionsByOrg) роблять `?? {}`.
   */
  permissions_by_org?: Record<string, string[]>;

  /**
   * Коди access-ролей user'а у поточному tenant'і (Порція Роздріб.6).
   *
   * Призначення: frontend POS-redirect логіка. Якщо user має тільки
   * 'CASHIER_POS' (без 'PROJECT_ADMIN' / 'CHIEF_ACCOUNTANT' тощо) —
   * SelectTenantPage авто-редіректить на /pos.
   *
   * • OWNER/ADMIN — завжди [] (немає user_access — це tenant-рівнева роль).
   * • MEMBER     — DISTINCT по ar.code з усіх ACTIVE user_access.
   * Поле optional для backward compat зі старими тестовими mock'ами.
   */
  access_role_codes?: string[];

  /**
   * ч.1119: ролі «лише перегляд». `read_only` — усі ролі користувача в цьому
   * акаунті лише для перегляду: сервер відхилить будь-яку зміну даних, тож
   * інтерфейс вимикає кнопки дій і показує позначку. `read_only_org_ids` —
   * організації, де так (користувач із різними ролями в різних організаціях).
   * Власник і адміністратор акаунта — завжди false / [].
   */
  read_only?: boolean;
  read_only_org_ids?: string[];
  /** ч.1126: тестовий акаунт — перенесення з 1С без підтвердження організації. */
  tenant_is_tester?: boolean;
}

/** ч.1074: чому акаунт зараз не відкриється (політика власника акаунта). */
export type AccessBlockReason = 'TWO_FACTOR_REQUIRED' | 'IP_NOT_ALLOWED';

/** Елемент списку GET /auth/my-tenants. */
export interface MyTenantItem {
  id: string;
  name: string;
  slug: string | null;
  tenant_role: 'OWNER' | 'ADMIN' | 'MEMBER';
  display_role: string;
  /** ч.1074: вхід у цей акаунт зараз заборонено (null — дозволено). */
  blocked_reason?: AccessBlockReason | null;
  /** ч.1074: поточна адреса — лише для відмови за мережею. */
  blocked_ip?: string | null;
  /** ч.1075: власний ярлик користувача для цього акаунта (бачить лише він). */
  label?: string | null;
}

/** ч.1075: зберегти власний ярлик акаунта (порожній — прибрати). */
export async function setTenantLabel(tenantId: string, label: string | null): Promise<void> {
  await api.put(`/auth/my-tenants/${tenantId}/label`, { label },
    { headers: { 'X-Silent-Toast': '1' } });
}

/**
 * POST /auth/login (Порція 3.0.8: API field 'login' → 'email').
 * Параметр функції зберігаємо як `loginValue` для backward compat у
 * викликачах; на бекенд шлемо як `email`.
 */
export async function login(loginValue: string, password: string): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>('/auth/login', {
    email: loginValue,
    password,
  });
  return data;
}

/**
 * Другий крок 2FA-логіну. Приймає одноразовий `mfa_token` з першого кроку
 * + 6-значний TOTP-код з Google Authenticator. Повертає звичайну пару токенів.
 */
export async function login2fa(mfaToken: string, code: string): Promise<TokenPair> {
  const { data } = await api.post<TokenPair>('/auth/login/2fa', {
    mfa_token: mfaToken,
    code,
  });
  return data;
}

/** Стан платформенного доступу + 2FA поточного user'а. */
export async function getPlatformStatus(): Promise<PlatformStatus> {
  const { data } = await api.get<PlatformStatus>('/auth/platform-status');
  return data;
}

/** Ініціалізація 2FA: генерує секрет + otpauth:// URI (НЕ вмикає ще). */
export async function totpSetup(): Promise<TotpSetupResult> {
  const { data } = await api.post<TotpSetupResult>('/auth/2fa/setup');
  return data;
}

/** Підтвердження першого коду → вмикає 2FA (204 No Content). */
export async function totpEnable(code: string): Promise<void> {
  await api.post('/auth/2fa/enable', { code });
}

export interface SignupRequest {
  /** ч.1425: згода з офертою і політикою; без неї сервер реєстрацію не приймає. */
  accept_terms: boolean;
  email: string;
  password: string;
  full_name: string;
  tenant_name: string;
  /** ч.1122: поле-пастка — у формі його не видно, людина лишає порожнім. */
  contact_time_pref?: string;
  /** ч.1126: код з посилання для тестувальників — акаунт одразу, без листа. */
  tester_code?: string;
}

/** Відповідь на запит реєстрації: лист із посиланням надіслано, акаунта ще немає. */
export interface SignupAccepted {
  status: 'confirmation_sent';
  email: string;
  expires_in_hours: number;
}

/** Реєстрація, крок 1 (ч.1122): лист із посиланням для підтвердження пошти.
 *  Обліковий запис створюється лише після підтвердження (confirmSignup). */
export async function signup(payload: SignupRequest): Promise<SignupAccepted | TokenPair> {
  const { data } = await api.post<SignupAccepted | TokenPair>(
    '/auth/signup', payload, { headers: { 'X-Silent-Toast': '1' } });
  return data;
}

/** ч.1126: чи чинне посилання для тестувальників (код з адреси сторінки). */
export async function checkTesterLink(code: string): Promise<{ valid: boolean }> {
  const { data } = await api.get<{ valid: boolean }>('/auth/signup/tester-link', { params: { code } });
  return data;
}

/** Чи дійсне посилання з листа (не використовує його). */
export async function checkSignup(token: string): Promise<{ valid: boolean; email: string | null }> {
  const { data } = await api.get<{ valid: boolean; email: string | null }>(
    '/auth/signup/check', { params: { token } });
  return data;
}

/** Реєстрація, крок 2: посилання з листа → обліковий запис створено, вхід виконано. */
export async function confirmSignup(token: string): Promise<TokenPair> {
  const { data } = await api.post<TokenPair>(
    '/auth/signup/confirm', { token }, { headers: { 'X-Silent-Toast': '1' } });
  return data;
}

/** ч.1397: refresh — у cookie; сервер відкликає його і стирає сесійні cookie. */
export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

export async function getMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/auth/me');
  return data;
}

/** Список tenant'ів, де user має ACTIVE membership. Глобальний endpoint
 *  (X-Tenant-Id ігнорується). */
export async function getMyTenants(): Promise<MyTenantItem[]> {
  const { data } = await api.get<MyTenantItem[]>('/auth/my-tenants');
  return data;
}

/**
 * Організація користувача у списку «по всіх кабінетах» (ч.1068, розумний
 * перемикач у шапці). Лише ідентифікація: організація + кабінет-власник.
 */
export interface MyOrganizationItem {
  tenant_id: string;
  tenant_name: string;
  id: string;
  name: string;
  code: string;
  /** ч.1406: тип і план (`EDO` — картка учасника без обліку). */
  entity_type?: 'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL';
  plan?: 'BASE' | 'EDO' | null;
  /** ч.1418: ЄДРПОУ чи РНОКПП; `code` — внутрішній код. */
  tax_code?: string | null;
}

/**
 * GET /auth/my-organizations — організації, доступні користувачу, по ВСІХ
 * його активних кабінетах (глобальний endpoint, X-Tenant-Id не враховується).
 * Правила доступу ті самі, що у GET /organizations кожного кабінету.
 */
export async function getMyOrganizations(): Promise<MyOrganizationItem[]> {
  const { data } = await api.get<MyOrganizationItem[]>('/auth/my-organizations');
  return data;
}

// ============== Відновлення пароля (ч.1073) ================================
// Публічні (без входу). Помилки сторінки показують самі — X-Silent-Toast.

/** Посилання на email і в Telegram (якщо підключено). Відповідь однакова для будь-якого email. */
export async function requestPasswordReset(email: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(
    '/auth/password-reset/request', { email }, { headers: { 'X-Silent-Toast': '1' } });
  return data;
}

/** Чи дійсне посилання (не використовує його). */
export async function checkPasswordReset(token: string): Promise<{ valid: boolean }> {
  const { data } = await api.get<{ valid: boolean }>(
    '/auth/password-reset/check', { params: { token } });
  return data;
}

/** Новий пароль за посиланням. Після — вихід на всіх пристроях. */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await api.post('/auth/password-reset/confirm',
    { token, new_password: newPassword }, { headers: { 'X-Silent-Toast': '1' } });
}

/** ч.1091: запит на скидання двофакторного входу — лист із КЕП (.p7s) оператору.
 *  Відповідь однакова для будь-якого email; результат — листом на адресу акаунта. */
export async function requestTwoFactorReset(
  email: string, letter: File, comment = '',
): Promise<{ detail: string }> {
  const fd = new FormData();
  fd.append('email', email);
  fd.append('letter', letter, letter.name);
  fd.append('comment', comment);
  const { data } = await api.post<{ detail: string }>(
    '/support/public/2fa-reset-request', fd, { headers: { 'X-Silent-Toast': '1' } });
  return data;
}

export interface CreateTenantRequest {
  tenant_name: string;
  billing_email?: string;
}

export interface CreateTenantResponse {
  id: string;
  name: string;
  slug: string | null;
}

/** Створює новий tenant для залогіненого user'а (Порція 3.0.5.0). */
export async function createTenant(
  payload: CreateTenantRequest,
): Promise<CreateTenantResponse> {
  const { data } = await api.post<CreateTenantResponse>(
    '/auth/create-tenant',
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// ч.958: «Обліковий запис» — безпека (пароль, 2FA) і сповіщення в Telegram.
// ---------------------------------------------------------------------------

export interface AccountSecurity {
  email: string;
  full_name: string;
  two_factor_enabled: boolean;
  /** Власнику платформи 2FA вимкнути не можна. */
  two_factor_required: boolean;
  /** ч.960: обраний спосіб і готовність каналів. */
  two_factor_method: 'NONE' | 'TOTP' | 'TELEGRAM';
  totp_ready: boolean;
  telegram_ready: boolean;
  telegram_chat_id: string | null;
  /** Чи доданий токен бота на сервері (без нього надсилання не працює). */
  telegram_bot_configured: boolean;
  telegram_bot_username: string | null;
  /** ч.1080 → ч.1405: збережений режим облікового запису — «ЕДО» / «Користувач» / «Бізнес» (ефективний — mode-context). */
  account_mode?: AccountMode;
  /** ч.1074: цей вхід пройшов другий крок (код застосунку/Telegram). */
  session_verified?: boolean;
  /** ч.1074: акаунти, де двофакторний вхід обовʼязковий для вас. */
  required_by?: { tenant_id: string; tenant_name: string; is_owner: boolean }[];
}

/** ч.1074: початок підтвердження входу кодом (без повторного входу). */
export interface StepUpStart {
  mfa_token: string;
  two_factor_method: 'TOTP' | 'TELEGRAM';
}

/** Нова пара (сесія пройшла другий крок) — cookie вже оновив сервер, фіксуємо ознаку сесії. */
function keepTokens(data: TokenPair | undefined): void {
  if (data) setSession(data.user_id ?? null);
}

export interface TotpSetup {
  secret: string;
  provisioning_uri: string;
  /** ч.960: готовий QR (SVG data-URI) — малюється на сервері. */
  qr_data_uri: string;
}

/** ч.1405: три режими облікового запису — «ЕДО» / «Користувач» / «Бізнес» (useAccountMode). */
export type AccountMode = 'EDO' | 'USER' | 'OWNER';
export interface ModeTenant { id: string; name: string; label: string | null; role?: string }
export interface ModeContext {
  /** Збережений режим ВЛАСНОГО акаунта (EDO | OWNER); «Користувач» = відкрито чужий акаунт (effectiveMode). */
  mode: AccountMode;
  can_user_mode: boolean;
  personal_tenant_id: string | null;
  own_tenants: ModeTenant[];
  foreign_tenants: ModeTenant[];
  labels: Record<AccountMode, string>;
}

export const account = {
  /** ч.1405: режим (збережений і ефективний), власний акаунт і чужі акаунти з доступом. */
  modeContext: () => api.get<ModeContext>('/auth/mode-context').then((r) => r.data),
  security: () =>
    api.get<AccountSecurity>('/auth/account-security').then((r) => r.data),
  /** ч.1090: після зміни пароля інші сесії відкликаються; ця — нова пара. */
  changePassword: (current_password: string, new_password: string) =>
    api.post<TokenPair>('/auth/change-password', { current_password, new_password })
      .then((r) => keepTokens(r.data)),
  /** ч.1111: «нагадати пароль» — одноразове посилання для нового пароля на
   *  власну пошту і в Telegram (якщо підключено). */
  remindPassword: () =>
    api.post<{ email: string; telegram: boolean; expires_in_minutes: number }>(
      '/auth/password-reset/self').then((r) => r.data),
  /** ч.1090: коли 2FA вже діє — переналаштування лише з паролем. */
  totpSetup: (password?: string) =>
    api.post<TotpSetup>('/auth/2fa/setup', password ? { password } : undefined)
      .then((r) => r.data),
  /** ч.1074: код щойно перевірено — сервер видає нову пару токенів (сесія
   *  пройшла другий крок); стару прибирає за нашим refresh-токеном. */
  totpEnable: (code: string) =>
    api.post<TokenPair>('/auth/2fa/enable', { code })
      .then((r) => keepTokens(r.data)),
  /** ч.1074: підтвердити поточний вхід кодом (акаунт вимагає 2FA). Для
   *  Telegram код надсилається одразу. */
  stepUpStart: () =>
    api.post<StepUpStart>('/auth/2fa/step-up/start', undefined,
      { headers: { 'X-Silent-Toast': '1' } }).then((r) => r.data),
  stepUp: (mfaToken: string, code: string) =>
    api.post<TokenPair>('/auth/2fa/step-up',
      { mfa_token: mfaToken, code: code.trim() },
      { headers: { 'X-Silent-Toast': '1' } }).then((r) => keepTokens(r.data)),
  /** ч.1080 → ч.1405: «ЕДО» / «Користувач» (лише з доступом до чужої організації) / «Бізнес». */
  setAccountMode: (mode: AccountMode) =>
    api.put('/auth/account-mode', { mode }).then(() => undefined),
  /** ч.960: обрати спосіб двофакторного входу. ч.1090: вимкнути — з паролем. */
  setTwoFactorMethod: (method: 'NONE' | 'TOTP' | 'TELEGRAM', password?: string) =>
    api.put('/auth/2fa/method', { method, password }).then(() => undefined),
  totpDisable: (password: string) =>
    api.post('/auth/2fa/disable', { password }).then(() => undefined),
  /** ч.959: кнопка «підключити» — посилання на бота з одноразовим кодом.
   *  ч.1090: коли 2FA діє — з паролем. */
  telegramLink: (password?: string) =>
    api.post<{ url: string; expires_in_minutes: number }>('/auth/telegram/link',
      password ? { password } : undefined).then((r) => r.data),
  /** ч.1090: коли 2FA діє — з паролем; сервер тоді повертає нову пару токенів. */
  setTelegram: (chat_id: string | null, password?: string) =>
    api.put<TokenPair | ''>('/auth/telegram', { chat_id, password })
      .then((r) => { if (r.data && typeof r.data === 'object') keepTokens(r.data); }),
  telegramTest: () => api.post('/auth/telegram/test').then(() => undefined),
};
